import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  FieldTypesEnum,
  SubCompanyRecipientModeEnum,
  subCompanyRecipientModeOptions,
} from 'constants/loanActionEditor';
import { LoanActionTargetType } from 'constants/loanActions';
import { loanContactTypesOptions, loanSenderTypesOptions } from 'constants/options';

import { selectDynamicFieldsOptions } from 'store/forms/selectors';
import {
  selectFormattedSecurityGroupOptions,
  selectSecurityGroupOptions,
} from 'store/global/selectors';
import {
  selectActiveLoanActionsTargetType,
  selectEmailTemplatesArray,
  selectFormattedLoanRolesOptions,
} from 'store/system/selectors';

import { FormAutocompleteOption } from '../../form/Autocomplete';
import TextFieldBuilder from '../../form/TextFieldBuilder';
import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import { getOptionCategoryName } from './LoanActionSelectOption.utils';
import { replaceSpanWithBrackets } from './LoanActionsGroup.utils';

// Recipient/sender option lists reuse `value` as a contact-TYPE discriminator
// (LoanRole / SecurityGroup), so `value` is NOT unique across the merged list — the
// unique id lives on loanRoleId / roleId (roles / security groups) or `value` (fixed
// contact types). Derive a stable unique `recipientKey` so these pickers key their
// options by identity, not by the shared category value.
const getRecipientOptionKey = option => option.loanRoleId ?? option.roleId ?? option.value;

const LoanActionEmailRow = ({ index, rootPath, isDisabled, isEditAllowed }) => {
  const emailTemplates = useSelector(selectEmailTemplatesArray);
  const dynamicFieldsOptions = useSelector(selectDynamicFieldsOptions);
  const loanRolesFormattedOptions = useSelector(selectFormattedLoanRolesOptions);
  const securityGroupFormattedOptions = useSelector(selectFormattedSecurityGroupOptions);
  const securityGroupOptions = useSelector(selectSecurityGroupOptions);
  const activeTargetType = useSelector(selectActiveLoanActionsTargetType);

  const { form, setInForm, validationErrors, validateField, handleChange } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const targetType = form.targetType || activeTargetType;
  const isSubCompanyTarget = targetType === LoanActionTargetType.SUB_COMPANY;
  const recipientsModeValue = _.get(form, `${fieldPath}.subCompanyRecipients.mode`)?.value;
  const isRoleRecipientsMode = recipientsModeValue === SubCompanyRecipientModeEnum.ROLE;
  const subjectFieldPath = `${fieldPath}.subject`;
  const subjectValue = _.get(form, subjectFieldPath) || '';
  const templateSubjectValue =
    replaceSpanWithBrackets(_.get(form, `${fieldPath}.templateId.subject`), dynamicFieldsOptions) ||
    '';
  const subjectValidationError = _.get(validationErrors, subjectFieldPath);

  const contactTypesOptions = useMemo(
    () =>
      [
        ...loanContactTypesOptions,
        ...loanRolesFormattedOptions,
        ...securityGroupFormattedOptions,
      ].map(option => ({ ...option, recipientKey: getRecipientOptionKey(option) })),
    [loanRolesFormattedOptions, securityGroupFormattedOptions],
  );

  const senderTypesOptions = useMemo(
    () =>
      [...loanSenderTypesOptions, ...loanRolesFormattedOptions].map(option => ({
        ...option,
        recipientKey: getRecipientOptionKey(option),
      })),
    [loanRolesFormattedOptions],
  );

  useEffect(() => {
    if (!templateSubjectValue) {
      return;
    }

    setInForm(subjectFieldPath, templateSubjectValue);
    validateField(subjectFieldPath, templateSubjectValue);
  }, [setInForm, subjectFieldPath, templateSubjectValue, validateField]);

  const onGroupOptions = useCallback(({ group }) => group, []);

  return (
    <div className={wrapperClassname}>
      <TextFieldBuilder
        fullWidth
        includeGroupName
        required
        data-testid={loanManagementTestIds.LOAN_MANAGEMENT_FORM_SUBJECT_INPUT}
        disabled={isDisabled || !isEditAllowed}
        error={!!subjectValidationError}
        groupBy={onGroupOptions}
        handleChange={handleChange}
        helperText={subjectValidationError}
        id={subjectFieldPath}
        label="Email Subject"
        name={subjectFieldPath}
        options={dynamicFieldsOptions}
        setInForm={setInForm}
        validateField={validateField}
        value={subjectValue}
      />
      <DynamicValueInput
        required
        disabled={isDisabled || !isEditAllowed}
        groupBy={getOptionCategoryName}
        label="Template"
        name={`${fieldPath}.templateId`}
        options={emailTemplates}
        type={FieldTypesEnum.SELECT}
      />
      <DynamicValueInput
        required
        OptionContent={FormAutocompleteOption}
        disabled={isDisabled || !isEditAllowed}
        groupBy={getOptionCategoryName}
        label="From"
        name={`${fieldPath}.sender`}
        optionKey="recipientKey"
        options={senderTypesOptions}
        type={FieldTypesEnum.SELECT}
      />
      <DynamicValueInput
        required
        OptionContent={FormAutocompleteOption}
        disabled={isDisabled || !isEditAllowed}
        label="Receivers"
        name={`${fieldPath}.receivers`}
        optionKey="recipientKey"
        options={contactTypesOptions}
        type={FieldTypesEnum.MULTISELECT}
      />
      <DynamicValueInput
        OptionContent={FormAutocompleteOption}
        disabled={isDisabled || !isEditAllowed}
        label="CC List"
        name={`${fieldPath}.ccList`}
        optionKey="recipientKey"
        options={contactTypesOptions}
        type={FieldTypesEnum.MULTISELECT}
      />
      <DynamicValueInput
        OptionContent={FormAutocompleteOption}
        disabled={isDisabled || !isEditAllowed}
        label="BCC List"
        name={`${fieldPath}.bccList`}
        optionKey="recipientKey"
        options={contactTypesOptions}
        type={FieldTypesEnum.MULTISELECT}
      />
      <DynamicValueInput
        disabled={isDisabled || !isEditAllowed}
        label="Tracking Status"
        name={`${fieldPath}.trackingStatus`}
        type={FieldTypesEnum.BOOLEAN}
      />
      {isSubCompanyTarget && (
        <>
          <DynamicValueInput
            disabled={isDisabled || !isEditAllowed}
            label="Sub-Company Recipients"
            name={`${fieldPath}.subCompanyRecipients.mode`}
            options={subCompanyRecipientModeOptions}
            type={FieldTypesEnum.SELECT}
          />
          {isRoleRecipientsMode && (
            <DynamicValueInput
              required
              disabled={isDisabled || !isEditAllowed}
              label="Recipients Role"
              name={`${fieldPath}.subCompanyRecipients.role`}
              options={securityGroupOptions}
              type={FieldTypesEnum.SELECT}
            />
          )}
        </>
      )}
    </div>
  );
};

LoanActionEmailRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
};

export default memo(LoanActionEmailRow);
